import React, { useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import { useTemplateOrCalculation } from '../../../hooks/useTemplateOrCalculation';
import { useCurrency } from '../../../hooks/useCurrency';
import { minimalToMinimum } from '@ydistri/utils';
import StatisticsPackagesIcon from '../../../../assets/statistics-packages.svg';
import StatisticsSection from './components/StatisticsSection';
import { sortByStatisticsType, StatisticsModalType } from './statisticsLib';
import TitleWithModalButton from './components/TitleWithModalButton';
import { computeRemSize } from '@ydistri/theme';
import { useGetStatisticsQuery, useGetStatsRouteSummariesQuery } from './apiCalculationStatistics';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../store';
import { capitalizeFirstLetter, formatNumber } from '@ydistri/generic-utils';
import { DataTable } from '@ydistri/ds';
import { TableRowDivider } from './lib/calculationStatsLib';

type RouteBaseStatistics =
  | {
      title: string;
      key: string;
      value: string | number;
    }
  | TableRowDivider;

type RouteMainStatistics =
  | {
      title: string;
      key: string;
      value: number;
      quantity: number;
      pickingPositionCount: number;
    }
  | TableRowDivider;

const routeBaseStatisticsColumns: ColumnsType<RouteBaseStatistics> = [
  {
    title: 'Title',
    dataIndex: 'title',
    key: 'title',
    width: computeRemSize(300),
  },
  {
    title: 'Value',
    dataIndex: 'value',
    key: 'value',
    width: computeRemSize(70),
    align: 'right',
  },
];

const getRouteMainStatisticsColumns = (currency: string): ColumnsType<RouteMainStatistics> => [
  {
    title: '',
    dataIndex: 'title',
    key: 'title',
    width: computeRemSize(130),
  },
  {
    title: 'Value',
    dataIndex: 'value',
    key: 'value',
    width: computeRemSize(130),
    align: 'right',
    render: (value: number) => `${formatNumber(value)} ${currency}`,
    sorter: (a, b) => ('value' in a && 'value' in b ? a.value - b.value : 0),
  },
  {
    title: 'Quantity',
    dataIndex: 'quantity',
    key: 'quantity',
    width: computeRemSize(130),
    align: 'right',
    render: (quantity: number) => `${formatNumber(quantity)} MU`,
    sorter: (a, b) => ('quantity' in a && 'quantity' in b ? a.quantity - b.quantity : 0),
  },
  {
    //in case of Routes, picking position is the same as product count (on api we get productCount, in header we display Picking positions
    title: 'Picking positions',
    dataIndex: 'pickingPositionCount',
    key: 'pickingPositionCount',
    width: computeRemSize(130),
    align: 'right',
    sorter: (a, b) =>
      'pickingPositionCount' in a && 'pickingPositionCount' in b
        ? a.pickingPositionCount - b.pickingPositionCount
        : 0,
  },
];

const SectionRoutes: React.FC = () => {
  const templateOrCalculation = useTemplateOrCalculation();
  const currencyId = useCurrency();
  const departmentId = useSelector((state: ReduxState) => state.statistics.departmentId);
  const clusterType = useSelector((state: ReduxState) => state.statistics.clusterType);

  const { data, isLoading } = useGetStatsRouteSummariesQuery({
    calculationId: templateOrCalculation.id,
    departmentId,
    clusterType,
  });

  const { data: overallStatisticsData, isFetching: isFetchingStatistics } = useGetStatisticsQuery({
    calculationId: templateOrCalculation.id,
    departmentId,
    clusterType,
  });

  const routeMainStatisticsColumns: ColumnsType<RouteMainStatistics> = useMemo(
    () => getRouteMainStatisticsColumns(currencyId),
    [currencyId],
  );

  const baseTableData: RouteBaseStatistics[] = useMemo(
    () =>
      overallStatisticsData
        ? [
            {
              title: 'Total number of routes',
              key: 'totalRoutes',
              value: formatNumber(overallStatisticsData.routesCount),
            },
            {
              divider: true,
              key: 'divider1',
            },
            {
              title: 'Avg. no. of outgoing routes',
              key: 'avgOutgoingRoutes',
              value: formatNumber(overallStatisticsData.avgOutgoingRoutes),
            },
            {
              title: 'Avg. no. of incoming routes',
              key: 'avgIncomingRoutes',
              value: formatNumber(overallStatisticsData.avgIncomingRoutes),
            },
          ]
        : [],
    [overallStatisticsData],
  );

  const mainTableData: RouteMainStatistics[] = useMemo(
    () =>
      [...(data ?? [])].sort(sortByStatisticsType).map(item => ({
        title: `${capitalizeFirstLetter(minimalToMinimum(item.type))} route`,
        key: `${item.type}Route`,
        value: item.value,
        quantity: item.quantity,
        pickingPositionCount: item.pickingPositionCount,
      })),
    [data],
  );

  return (
    <StatisticsSection icon={StatisticsPackagesIcon}>
      <TitleWithModalButton title="Routes" modalType={StatisticsModalType.ROUTE} />
      <DataTable<RouteBaseStatistics>
        id="route-base-statistics"
        rowKey="key"
        dataSource={baseTableData}
        columns={routeBaseStatisticsColumns}
        loading={isFetchingStatistics}
      />
      <DataTable<RouteMainStatistics>
        id="route-main-statistics"
        rowKey="key"
        dataSource={mainTableData}
        columns={routeMainStatisticsColumns}
        loading={isLoading}
        showHeader={true}
      />
    </StatisticsSection>
  );
};

export default SectionRoutes;
